import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { asinTableColumnDefinitions } from "./asinValidationTable-config";
import { AppThunk } from "../app/store";
import { reportValidationResult, validateAsinMarketplaces } from "../app/api";
import {
  FullAsinWithAudits,
  RestrictedAsin,
} from "../../server/asin-central/model";
import {
  addNotificationWithKey,
  createNotificationDefinition,
  removeNotification,
  VALIDATION_STATUS_NOTIFICATION_KEY,
} from "../notification/notificationConnector";
import { porKey } from "../porAsinList/PorAsinListTable-config";
import { ValidationStatus } from "@amzn/coral_com-amazon-asincentral-common";
import logger from "../../art-logger-config";
import { getFromLocalStorage } from "../../util/localStorageUtils";
import { IValidationService } from "./validationAttribute";
import { ManualValidationResultAttribute } from "./validationListEdit";

interface AsinValidationTableControlState {
  preferences: {
    pageSize: number;
    visibleContent: string[];
  };
  sort?: { sortingField: string };
  descending: boolean;
  validationInProgress: boolean;
  validationError: string | null;
  refreshFlipper: boolean;
  // Editing
  edited: boolean;
  isEditing: boolean;
  editedRowSet: { [index: number]: boolean };
  editableValidationAsins: Array<FullAsinWithAudits | RestrictedAsin>;
  attributeIndexMap: { [asinMp: string]: number };
  pageNumber: number;
  selectedValidationService: IValidationService | null;
}

function getInitialPreferences() {
  const defaultInitialPreferences = {
    pageSize: 30,
    visibleContent: asinTableColumnDefinitions.options.flatMap((item) =>
      item.options.filter((c) => c.defaultVisible).map((c) => c.id)
    ),
  };

  try {
    const localPrefsString = getFromLocalStorage(
      "VALIDATION_TABLE_PREFERENCES"
    );
    if (localPrefsString !== null) {
      return JSON.parse(localPrefsString);
    }
  } catch (e: unknown) {
    if (e instanceof Error) {
      logger.error(
        `Failed to load initial preferences for validation table: ${e.toString()}`
      );
    } else {
      logger.error("an unknown error occurred");
    }
  }
  return defaultInitialPreferences;
}

const asinValidationTableControlInitialState: AsinValidationTableControlState =
  {
    preferences: getInitialPreferences(),
    descending: true,
    refreshFlipper: false,
    validationInProgress: false,
    validationError: null,
    edited: false,
    isEditing: false,
    editedRowSet: {},
    editableValidationAsins: [],
    attributeIndexMap: {},
    pageNumber: 1,
    selectedValidationService: null,
  };

function updateEditedValidationEntry(
  _state: AsinValidationTableControlState,
  attribute: ManualValidationResultAttribute,
  value: string,
  entryToUpdate: FullAsinWithAudits
): FullAsinWithAudits {
  const updatedEntry = { ...entryToUpdate };
  updatedEntry[attribute] = value;
  return updatedEntry;
}

const asinValidationTableControl = createSlice({
  name: "asinValidationTableControl",
  initialState: asinValidationTableControlInitialState,
  reducers: {
    setPreferences(state, action) {
      state.preferences.pageSize = action.payload.pageSize;
      state.preferences.visibleContent = action.payload.visibleContent;
    },
    mergeVisibleContentPreferences(state, action) {
      const activeColumns = action.payload?.activeColumns ?? [];
      state.preferences.visibleContent = Array.from(
        new Set<string>([...state.preferences.visibleContent, ...activeColumns])
      );
    },
    setSort(state, action) {
      state.sort = action.payload.sortingColumn;
      state.descending = action.payload.isDescending;
    },
    validateAsinMarketplacesStart(state) {
      state.validationInProgress = true;
    },
    validateAsinMarketplacesSuccess(state) {
      state.validationInProgress = false;
    },
    validateAsinMarketplacesFailure(state, action: PayloadAction<string>) {
      state.validationInProgress = false;
      state.validationError = action.payload;
    },
    setEditingValidationTable(state, { payload }) {
      state.isEditing = true;
      state.editedRowSet = {};
      state.editableValidationAsins = [...payload.currentPagePorAsins];
      state.editableValidationAsins.forEach((value, index) => {
        state.attributeIndexMap[porKey(value)] = index;
      });
      state.selectedValidationService = payload.validationService;
    },
    setViewingValidationTable(state) {
      state.isEditing = false;
      state.attributeIndexMap = {};
      state.edited = false;
      state.editableValidationAsins = [];
      state.selectedValidationService = null;
      state.refreshFlipper = !state.refreshFlipper;
    },
    triggerValidationDashboardRefresh(state) {
      state.refreshFlipper = !state.refreshFlipper;
    },
    updateValidationAttribute(state, action) {
      const [entryToUpdate, attribute, value] = action.payload;
      const updatedEntry = updateEditedValidationEntry(
        state,
        attribute,
        value,
        entryToUpdate
      );
      state.editedRowSet[state.attributeIndexMap[porKey(entryToUpdate)]] = true;
      state.editableValidationAsins[
        state.attributeIndexMap[porKey(entryToUpdate)]
      ] = updatedEntry;
      state.edited = true;
    },
    bulkUpdateValidationAttributes(state, action) {
      const [attribute, value] = action.payload;
      state.editableValidationAsins = state.editableValidationAsins.map(
        (entryToUpdate) => {
          if (!entryToUpdate.isRestricted) {
            state.editedRowSet[state.attributeIndexMap[porKey(entryToUpdate)]] =
              true;
            return updateEditedValidationEntry(
              state,
              attribute,
              value,
              entryToUpdate
            );
          }
          return entryToUpdate;
        }
      );
      state.edited = Object.keys(state.editedRowSet).length > 0;
    },
    setValidationPageNumber(state, action) {
      state.pageNumber = action.payload.pageNumber;
    },
  },
});

export const {
  setPreferences,
  mergeVisibleContentPreferences,
  setSort,
  validateAsinMarketplacesStart,
  validateAsinMarketplacesFailure,
  validateAsinMarketplacesSuccess,
  setEditingValidationTable,
  setViewingValidationTable,
  updateValidationAttribute,
  bulkUpdateValidationAttributes,
  setValidationPageNumber,
  triggerValidationDashboardRefresh,
} = asinValidationTableControl.actions;

export default asinValidationTableControl.reducer;

export const fetchValidateAsinMarketplaces =
  (
    asins: (FullAsinWithAudits | RestrictedAsin)[],
    validationService: IValidationService | null,
    queryString?: string
  ): AppThunk =>
  async (dispatch) => {
    try {
      if (validationService === null) {
        throw new Error("validationService was null, unable to validate.");
      }
      const serviceName = validationService.serviceName;
      const nonRestrictedAsins = asins.filter(
        (asin) =>
          (asin as FullAsinWithAudits) &&
          !asin.isRestricted &&
          Object.prototype.hasOwnProperty.call(asin, "asin") &&
          Object.prototype.hasOwnProperty.call(asin, "marketplace")
      ) as FullAsinWithAudits[];

      dispatch(validateAsinMarketplacesStart());
      dispatch(
        addNotificationWithKey({
          key: VALIDATION_STATUS_NOTIFICATION_KEY,
          message: {
            type: "info",
            header: `${serviceName} Validation Pending`,
            dismissible: true,
            content: "Validation is starting...",
          },
        })
      );

      const result = await validateAsinMarketplaces(
        nonRestrictedAsins,
        validationService.serviceId,
        validationService.isManual,
        queryString
      );

      if (
        result === undefined ||
        result.validateAsinMarketplacesOutput === undefined ||
        result.validateAsinMarketplacesOutput.validations === undefined
      ) {
        throw new Error(
          "Failed to parse response from validateAsinMarketplaces."
        );
      }

      dispatch(validateAsinMarketplacesSuccess());
      const aggregatedRunIds = result.validateAsinMarketplacesOutput.validations
        .map((validation) => validation.id)
        .join(", ");
      dispatch(
        addNotificationWithKey({
          key: VALIDATION_STATUS_NOTIFICATION_KEY,
          message: {
            type: "success",
            header: `${serviceName} Validation Started`,
            dismissible: true,
            content: `${serviceName} validation has started. This may take up to 10 minutes to complete. You will receive an email when validation completes. ${serviceName} Validation IDs: ${aggregatedRunIds}`,
          },
        })
      );

      if (validationService.isManual) {
        const validationRuns =
          result.validateAsinMarketplacesOutput.validations;
        const runCount = validationRuns.length;
        for (let i = 0; i < runCount; i++) {
          dispatch(
            addNotificationWithKey({
              key: VALIDATION_STATUS_NOTIFICATION_KEY,
              message: {
                type: "info",
                header: `${serviceName} Reporting Validation`,
                dismissible: true,
                content: `Reporting validation run ${i + 1} of ${runCount}`,
              },
            })
          );
          const validation =
            result.validateAsinMarketplacesOutput.validations[i];
          if (validation.targets === undefined || validation.id === undefined) {
            throw new Error("Validation response has undefined elements.");
          }
          await reportValidationResult(
            validation.targets,
            nonRestrictedAsins,
            validation.id,
            ValidationStatus.COMPLETED,
            validationService.serviceId
          );
        }
        dispatch(
          addNotificationWithKey({
            key: VALIDATION_STATUS_NOTIFICATION_KEY,
            message: {
              type: "success",
              header: `${serviceName} Validation Completed`,
              dismissible: true,
              content: `${serviceName} validation reporting has succeeded for ${nonRestrictedAsins.length} ASINs.`,
            },
          })
        );
      }
    } catch (err: unknown) {
      // Non-Error rejections must clean up state too. The pending
      // notification is keyed on VALIDATION_STATUS_NOTIFICATION_KEY,
      // which is never auto-cleared, so leaving it in place would
      // leave the "Validation Pending" flashbar stuck indefinitely
      // and validationInProgress stuck at true.
      const detail =
        err instanceof Error ? err.toString() : "an unknown error occurred";
      if (!(err instanceof Error)) {
        logger.error(detail);
      }
      dispatch(validateAsinMarketplacesFailure(detail));
      dispatch(removeNotification(VALIDATION_STATUS_NOTIFICATION_KEY));
      dispatch(
        createNotificationDefinition(
          "error",
          "Validation Start Failure",
          true,
          `Validation start failed with exception: ${detail}`
        )
      );
    }
  };
